import type { Dictionary } from "../types";

/**
 * Ukrainian. Note the plural entries carry `few` (2–4) and `many` (0, 5–20)
 * in addition to `one` — Intl.PluralRules resolves which applies.
 */
export const uk: Dictionary = {
  common: {
    appName: "Retrospecter",
    poweredBy: "Створено за підтримки",
    loading: "Завантаження…",
    cancel: "Скасувати",
    save: "Зберегти",
    saving: "Збереження…",
    returnHome: "На головну",
    yours: "Ваша",
    by: "від {name}",
  },

  nav: {
    logOut: "Вийти",
    switchToDark: "Перемкнути на темну тему",
    switchToLight: "Перемкнути на світлу тему",
    darkTheme: "Темна тема",
    lightTheme: "Світла тема",
    language: "Мова",
  },

  login: {
    title: "Вхід",
    subtitle: "Доступ персоналу до ретроспектив Retrospecter",
    redirectedNotice:
      "Ця ретроспектива доступна лише для персоналу. Увійдіть, щоб продовжити.",
    username: "Пошта або ім'я користувача",
    usernamePlaceholder: "im'ya@company.com",
    password: "Пароль",
    submit: "Увійти",
    submitting: "Вхід…",
    invalidCredentials: "Неправильне ім'я користувача або пароль.",
    providerUnavailable:
      "Служба автентифікації зараз недоступна. Спробуйте ще раз за кілька хвилин.",
    anonymousHint:
      "Ретроспективи, що тривають, відкриті для всіх, хто має посилання — вхід не потрібен.",
  },

  home: {
    title: "Ретроспективи",
    subtitle: "Почніть нову сесію або відкрийте ту, яку команда вже провела.",
    newRetro: "Нова ретроспектива",
    nameLabel: "Назва ретроспективи",
    namePlaceholder: "напр. Ретро спринту 42",
    create: "Створити ретроспективу",
    creating: "Створення…",
    createError: "Не вдалося створити ретроспективу.",
    draftHint:
      "Нові ретроспективи починаються як приватний чернетковий запис. Активуйте її, щоб поділитися посиланням із командою.",
  },

  create: {
    title: "Нова ретроспектива",
    subtitle: "Назвіть сесію та оберіть формат, у якому працюватиме команда.",
    back: "Назад до ретроспектив",
    chooseTemplate: "Оберіть формат",
    chooseHint: "Кожен попередній перегляд показує колонки, які побачить команда.",
    selected: "Обрано",
    columnCount: {
      one: "{count} колонка",
      few: "{count} колонки",
      many: "{count} колонок",
      other: "{count} колонки",
    },
    loadError: "Не вдалося завантажити шаблони. Оновіть сторінку, щоб спробувати ще раз.",
    error: "Не вдалося створити ретроспективу.",
  },

  list: {
    heading: "Ваші дошки",
    regionLabel: "Ретроспективи",
    filterLabel: "Фільтрувати ретроспективи",
    filterAll: "Усі",
    filterPast: "Минулі",
    filterRunning: "Активні",
    filterDrafts: "Чернетки",
    empty: "Ретроспектив ще немає — створіть першу вгорі.",
    emptyFiltered: "Тут поки що порожньо.",
    loadError: "Не вдалося завантажити ретроспективи. Оновіть сторінку, щоб спробувати ще раз.",
    createdOn: "Створено {date}",
    completedOn: "Завершено {date}",
    notes: {
      one: "{count} нотатка",
      few: "{count} нотатки",
      many: "{count} нотаток",
      other: "{count} нотатки",
    },
    participants: {
      one: "{count} учасник",
      few: "{count} учасники",
      many: "{count} учасників",
      other: "{count} учасника",
    },
  },

  board: {
    loading: "Завантаження ретроспективи…",
    accessDeniedTitle: "Доступ заборонено",
    accessDeniedBody:
      "Ви увійшли, але не маєте дозволу переглядати цю ретроспективу.",
    notFoundTitle: "Не знайдено",
    notFoundBody: "Ретроспективи, яку ви шукаєте, не існує.",
    activate: "Активувати",
    activating: "Активація…",
    complete: "Завершити",
    completing: "Завершення…",
    joinPrompt: "Приєднайтесь, щоб додавати нотатки, голосувати й упорядковувати дошку.",
    join: "Приєднатися до ретроспективи",
    joining: "Приєднання…",
    joinError: "Не вдалося приєднатися до цієї ретроспективи.",
    draftNotice:
      "Ця ретроспектива ще чернетка — її бачите лише ви. Активуйте її, щоб поділитися посиланням із командою.",
    dragHint:
      "Порада: перетягуйте стікери куди завгодно, тягніть за кут, щоб змінити розмір, і кладіть один на одного, щоб згрупувати.",
    readOnly: "Цю ретроспективу завершено, вона доступна лише для читання",
    participantsRegion: "Учасники",
    participantsHeading: "Учасники ({count})",
    noParticipants: "Ще ніхто не приєднався.",
    statusDraft: "ЧЕРНЕТКА",
    statusActive: "АКТИВНА",
    statusCompleted: "ЗАВЕРШЕНА",
  },

  column: {
    empty: "Тут поки що порожньо — киньте нотатку будь-де",
    pileLabel: "Візьміть нотатку зі стосу",
    pileHint: "Перетягніть нотатку в колонку або натисніть Enter, щоб обрати її.",
    pileChoose: "Додати нотатку до колонки {column}",
    dropHere: "Відпустіть, щоб додати нотатку",
    addNote: "Додати нотатку",
    addNoteTo: "Додати нотатку до колонки {column}",
    newNoteFor: "Нова нотатка для колонки {column}",
    notePlaceholder: "Поділіться думками…",
    saveNote: "Зберегти нотатку",
    adding: "Додавання…",
    cancelNote: "Скасувати нотатку",
  },

  note: {
    yourNote: "Ви",
    react: "Відреагувати {emoji}",
    unreact: "Прибрати вашу реакцію {emoji}",
    addReaction: "Додати реакцію",
    edit: "Редагувати",
    editNote: "Редагувати нотатку",
    delete: "Видалити",
    deleteNote: "Видалити нотатку",
    vote: "Проголосувати за цю нотатку",
    unvote: "Прибрати свій голос",
    voteHint: "Приєднайтесь до ретроспективи, щоб голосувати",
    move:
      "Перемістити нотатку. Стрілки рухають її по колонці; із Shift вона переходить до сусідньої колонки.",
    moveHint: "Перетягніть або сфокусуйте й використовуйте стрілки",
    resize: "Змінити розмір нотатки. Використовуйте стрілки, щоб налаштувати його.",
    resizeHint: "Потягніть за кут або сфокусуйте й використовуйте стрілки",
    draggable: "Нотатка, яку можна перетягувати",
    saveError: "Не вдалося зберегти зміну.",
    deleteError: "Не вдалося видалити нотатку.",
    voteError: "Не вдалося зарахувати ваш голос.",
    movedToColumn: "Переміщено до {column}",
    movedTo: "Переміщено на {x} праворуч і {y} вниз у {column}",
    groupWithPrevious: "Згрупувати з нотаткою вище",
    ungroup: "Вийняти з групи",
    ungroupError: "Не вдалося вийняти нотатку з групи.",
    groupError: "Не вдалося згрупувати нотатки.",
    dropToGroup: "Відпустіть, щоб згрупувати",
    groupSummary: "{votes} голосів у групі",
    groupedNotes: {
      one: "{count} нотатка",
      few: "{count} нотатки",
      many: "{count} нотаток",
      other: "{count} нотатки",
    },
  },

  actions: {
    heading: "Дії",
    regionLabel: "Узгоджені дії",
    intro: "Те, що команда домовилася зробити.",
    empty: "Поки нічого не домовлено.",
    textLabel: "Що ми зробимо?",
    textPlaceholder: "Розділити конвеєр розгортання на два",
    ownerLabel: "Хто відповідальний?",
    ownerPlaceholder: "Ім'я (необов'язково)",
    unassigned: "Без відповідального",
    add: "Додати",
    adding: "Додаємо…",
    markDone: "Позначити як виконане",
    markNotDone: "Позначити як невиконане",
    delete: "Видалити дію",
    saveError: "Не вдалося зберегти дію.",
    carriedHeading: "З минулого разу",
    carriedIntro: "Досі відкрито з попередньої ретроспективи.",
    carriedFrom: "З «{name}»",
    carryOver: "Перенести сюди",
    carriedCount: {
      one: "{count} відкрита дія",
      few: "{count} відкриті дії",
      many: "{count} відкритих дій",
      other: "{count} відкритої дії",
    },
  },

  phase: {
    label: "Етап",
    brainstorm: "Ідеї",
    grouping: "Групування",
    voting: "Голосування",
    discussion: "Обговорення",
    error: "Не вдалося змінити етап.",
  },

  timer: {
    label: "Таймер",
    addMinutes: "+{count} хв",
    minutes: "{count} хв",
    stop: "Зупинити",
    finished: "Час вийшов",
  },

  notFound: {
    title: "Сторінку не знайдено",
    body: "Ой! Сторінки, яку ви шукаєте, не існує. Повернімося на правильний шлях.",
  },

  templates: {
    "Start/Stop/Continue": {
      name: "Почати/Припинити/Продовжити",
      description: "Стандартний формат. Конкретні зміни: що почати, що припинити, що лишити.",
      columns: { Start: "Почати", Stop: "Припинити", Continue: "Продовжити" },
      hints: {
        "Start": "Те, чого ми ще не робимо, але це допомогло б.",
        "Stop": "Те, що коштує нам часу чи настрою — варто припинити.",
        "Continue": "Те, що вже працює — назвіть, щоб не втратити.",
      },
    },
    "Mad/Sad/Glad": {
      name: "Злість/Сум/Радість",
      description: "Показує, як команда пережила спринт, перш ніж обговорювати дії.",
      columns: { Mad: "Злість", Sad: "Сум", Glad: "Радість" },
      hints: {
        "Mad": "Що дратувало або злило вас цього спринту.",
        "Sad": "Що розчарувало або не виправдало сподівань.",
        "Glad": "Що потішило або вийшло краще, ніж очікували.",
      },
    },
    "What Went Well": {
      name: "Що вдалося",
      description: "Простий огляд, що завершується домовленостями.",
      columns: {
        "What went well": "Що вдалося",
        "What didn't": "Що не вдалося",
        "Action items": "Дії",
      },
      hints: {
        "What went well": "Успіхи, які варто повторити, навіть найменші.",
        "What didn't": "Де було важко — опишіть проблему, а не людину.",
        "Action items": "Конкретний наступний крок, бажано з відповідальним.",
      },
    },
    "Rose/Bud/Thorn": {
      name: "Троянда/Пуп'янок/Шип",
      description: "Врівноважує успіхи, проблеми й те, що лише починає визрівати.",
      columns: { Rose: "Троянда", Bud: "Пуп'янок", Thorn: "Шип" },
      hints: {
        "Rose": "Явний успіх або момент, вартий святкування.",
        "Bud": "Рання ідея чи можливість, яку варто плекати.",
        "Thorn": "Проблема чи перешкода, що завдала шкоди.",
      },
    },
    "4 Ls": {
      name: "4 Л",
      description: "Відділяє емоції від висновків. Корисно після важкого спринту.",
      columns: {
        Liked: "Сподобалось",
        Learned: "Навчились",
        Lacked: "Бракувало",
        "Longed for": "Хотілося б",
      },
      hints: {
        "Liked": "Що вам сподобалось у тому, як ми працювали.",
        "Learned": "Нове знання або усвідомлення, яке ви здобули.",
        "Lacked": "Чого бракувало: інструментів, часу, ясності, підтримки.",
        "Longed for": "Чого вам хотілося, але не мали.",
      },
    },
    Sailboat: {
      name: "Вітрильник",
      description: "Візуальний формат: що рухає команду вперед, а що стримує.",
      columns: {
        "Wind (helps us)": "Вітер (допомагає)",
        "Anchors (slows us)": "Якорі (стримують)",
        "Rocks (risks)": "Рифи (ризики)",
        "Island (goal)": "Острів (мета)",
      },
      hints: {
        "Wind (helps us)": "Що штовхало нас уперед і додавало швидкості.",
        "Anchors (slows us)": "Що тягнуло нас назад і сповільнювало.",
        "Rocks (risks)": "Небезпеки попереду, які зашкодять, якщо їх ігнорувати.",
        "Island (goal)": "Куди ми насправді прагнемо дістатися.",
      },
    },
    KALM: {
      name: "KALM",
      description: "Впорядковує практики: лишити, додати, менше або більше.",
      columns: {
        Keep: "Лишити",
        Add: "Додати",
        "Less of": "Менше",
        "More of": "Більше",
      },
      hints: {
        "Keep": "Працює як є — убережіть від змін.",
        "Add": "Щось нове, що варто спробувати.",
        "Less of": "Справа корисна, але ми з нею перебираємо.",
        "More of": "Працює добре — варто робити більше.",
      },
    },
    Starfish: {
      name: "Морська зірка",
      description:
        "Детальніше за Почати/Припинити/Продовжити; підходить зрілим командам.",
      columns: {
        "Keep doing": "Продовжувати",
        "More of": "Робити більше",
        "Less of": "Робити менше",
        "Start doing": "Почати робити",
        "Stop doing": "Припинити робити",
      },
      hints: {
        "Keep doing": "Ефективно саме так — не змінюйте.",
        "More of": "Вже допомагає; виграємо, якщо робитимемо більше.",
        "Less of": "Не шкідливо, але цього забагато.",
        "Start doing": "Нова практика, яку варто спробувати.",
        "Stop doing": "Не дає користі або відверто заважає.",
      },
    },
  },
};
